import React, { FC, useMemo, useEffect } from 'react';
import {
  useCurrentProject,
  useProjectsProvider,
} from '@material-bank/projects-utils';
import { AnalyticsEventLocation, StructuredProject } from '@mb/lib';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import styles from './productGrid.module.scss';
import { sendAnalytics } from '../../../api/events';
import {
  ProductCartClickProps,
  ProjectAnalyticsProps,
} from '../../../components/Cards/ResultCard/types';
import { getEventsMaterialProps } from '../../../helpers';
import useAddToBagWithValidations from '../../../hooks/useAddToBagWithValidations';
import { useViewScrollableProductsListEvent } from '../../../hooks/useViewScrollableProductsListEvent';
import { cartItemsSelector } from '../../../redux/selectors/commonSelector';
import {
  allCurrentPrimaryColorsLabelsSelector,
  appFiltersSelector,
  searchToggleSelector,
} from '../../../redux/selectors/filtersSelector';
import {
  isSearchRedirectSelector,
  searchOptionsSelector,
  selectedVariantsSelector,
  xRequestIdSelector,
} from '../../../redux/selectors/searchSelector';
import { removeCartItemFromQueue } from '../../../redux/sliceCreators/commonSlice';
import { CartItem, ProductSearchMaterial } from '../../../types';
import {
  getTaxonomyAnalyticsDataFromFilters,
  handleAddToBoards,
  pushSearchAnalyticsDataLayer,
} from '../../../utils/helpers';
import { ResultCardWrapper } from '../../MaterialsContainer/ResultCardWrapper';
import { getProductCardLabels } from '../utilities/getProductCardLabels';

const activeBagTime = 2500;

interface ProductGridProps {
  products: ProductSearchMaterial[];
  indexLocation: number;
  blockId: string;
  handleProductSelect: (product: ProductSearchMaterial) => void;
  selectedProducts?: ProductSearchMaterial[];
  handleProjectClick?: (
    projectid?: number,
    options?: { analyticsEventLocation?: string | undefined },
  ) => void;
  onProjectSelect?: (projectId: number) => void;
  selectedProject: StructuredProject;
  onCreateProjectClick?: (props: ProjectAnalyticsProps) => void;
}

export const ProductGrid: FC<ProductGridProps> = ({
  products,
  indexLocation,
  blockId,
  handleProductSelect,
  selectedProducts = [],
  handleProjectClick,
  onProjectSelect,
  selectedProject,
  onCreateProjectClick,
}) => {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery({ query: '(max-width: 1024px)' });

  const searchOptions = useSelector(searchOptionsSelector);
  const searchToggle = useSelector(searchToggleSelector);
  const { activeProjects } = useProjectsProvider();
  const xRequestId = useSelector(xRequestIdSelector);
  const cartItems = useSelector(cartItemsSelector);
  const appFilters = useSelector(appFiltersSelector);
  const isSearchRedirect = useSelector(isSearchRedirectSelector);
  const currentSelectedColors = useSelector(
    allCurrentPrimaryColorsLabelsSelector,
  );
  const { currentProject } = useCurrentProject();
  const { handleAddToBag, loadingIds } = useAddToBagWithValidations();

  const handleContextMenuClick = () => {};

  const handleOnColorVariantsClick = () => {};
  const selectedVariants = useSelector(selectedVariantsSelector);
  const intl = useIntl();

  const { id: itemListId, title: itemListName } = useMemo(() => {
    return getTaxonomyAnalyticsDataFromFilters(appFilters);
  }, [appFilters]);

  const handleViewDataLayer = (products: ProductSearchMaterial[]) => {
    const currentColorsList = currentSelectedColors?.join(', ');
    pushSearchAnalyticsDataLayer({
      payload: products,
      currentColor: currentColorsList,
      listId: itemListId,
      listTitle: itemListName,
      eventName: 'view_item_list',
      eventLocation: '',
      isFromCatalog: true,
      isSearch: isSearchRedirect,
      project: currentProject,
    });
  };

  const { onProductInViewChange } = useViewScrollableProductsListEvent({
    sendAnalyticsEvent: handleViewDataLayer,
  });

  const cardStaticLabels = useMemo(() => {
    return getProductCardLabels(intl);
  }, [intl]);

  const cartItemsIds = useMemo(() => {
    return cartItems?.map((item: CartItem) => item.entityId) || [];
  }, [cartItems]);

  // If something was added to Cart, show isAdded icon for X secconds,
  // and then remove icon
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const oldCartItems = cartItems?.filter(
        (item: CartItem) => now - item.time >= activeBagTime,
      );
      if (oldCartItems?.length > 0) {
        dispatch(removeCartItemFromQueue(oldCartItems));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [cartItems]);

  const multiselectIds = useMemo(() => {
    const relevantList = selectedProducts;
    return (relevantList ?? []).map(
      (item: ProductSearchMaterial) => item.entityId,
    );
  }, [selectedProducts]);

  const structuredProjects = useMemo(() => {
    return Object.values(activeProjects).map((project) => ({
      ...project,
      id: project.externalId,
      label: project.title,
    }));
  }, [activeProjects]);

  const handleMaterialSelect = (product: ProductSearchMaterial) => {
    handleProductSelect(product);
  };

  const handleAddToCart = ({
    product,
    productEventData,
  }: ProductCartClickProps) => {
    handleAddToBag(
      {
        product,
        productEventData,
        isMobile,
        analyticsEventLocation: AnalyticsEventLocation.CATALOG,
        relatedProductData: undefined,
        shouldTriggerRecModal: undefined,
        additionalSplitProperties: {},
        listId: `contentblocks_catalog_${blockId}_${indexLocation}`,
        listName: `contentblocks catalog ${blockId} ${indexLocation}`,
      },
      currentProject,
    );
  };

  const handleBoards = async (
    product: ProductSearchMaterial,
    index: number,
  ) => {
    await handleAddToBoards({
      product,
      index,
      itemListId: `contentblocks_catalog_${blockId}_${indexLocation}`,
      itemListName: `contentblocks catalog ${blockId} ${indexLocation}`,
      analyticsEventLocation: AnalyticsEventLocation.CATALOG,
      project: currentProject,
    });
  };

  const handleProductClick = async (
    product: ProductSearchMaterial,
    _index = 0,
    withAnalytics = true,
  ) => {
    //Analytics
    if (withAnalytics) {
      const analyticsProps = getEventsMaterialProps(product);
      await sendAnalytics(
        {
          id: [String(product?.entityId)],
          type: 'view',
          ...analyticsProps,
        },
        1200,
      );
    }
    dataLayer.push({
      event: 'view_item_list',
      list_id: `contentblocks_catalog_${blockId}_${indexLocation}`,
      list_name: `contentblocks catalog ${blockId} ${indexLocation}`,
      event_location: 'Catalog',
      item_list_id: `contentblocks_catalog_${blockId}_${indexLocation}`,
      item_list_name: `contentblocks catalog ${blockId} ${indexLocation}`,
    });

    if (!isMobile) {
      window.dispatchEvent(
        new CustomEvent('initMiniPDP', {
          detail: {
            id: product.entityId,
            product,
            isProduct: product.isProducts,
            isMaterial: !product.isProducts,
            clickedProductGridIndex: product.gridIndex,
            clickedProductRequestId: product.originRequestId || xRequestId,
            clickedProductAnalyticsClass: product.analyticsData?.analyticsClass,
            clickedProductAnalyticsImpressions:
              product.analyticsData?.impressions,
            dataLayerAnalytics: product.analyticsData?.dataLayerAnalytics,
          },
        }),
      );
    } else {
      window.location.href = product.url;
    }
  };

  const renderItem = (index: number) => {
    const rawMaterial = products[index];
    const material = {
      ...rawMaterial,
      gridIndex: index + 1,
    };

    const isMultiSelected =
      (multiselectIds ?? []).find(
        (id: string) =>
          id === material?.entityId ||
          id === material?.selectedColorFilterEntityId,
      ) != null;

    return (
      <ResultCardWrapper
        isVirtualized={false}
        key={material?.sku}
        isSearchPage={true}
        material={material}
        searchOptions={searchOptions}
        searchToggle={searchToggle}
        cardStaticLabels={cardStaticLabels}
        // this kicks off the select mode
        isMultiselect={selectedProducts.length > 0}
        isSelected={isMultiSelected}
        onMaterialSelect={handleMaterialSelect}
        multiselectIds={multiselectIds}
        imageSize={252}
        projects={structuredProjects}
        selectedProject={selectedProject}
        cartItemsIds={cartItemsIds}
        onProductClick={handleProductClick}
        onCreateProjectClick={onCreateProjectClick}
        onProjectClick={handleProjectClick}
        isMobile={isMobile}
        onContextMenuClick={handleContextMenuClick}
        itemListId={`contentblocks_catalog_${blockId}_${indexLocation}`}
        itemListName={`contentblocks catalog ${blockId} ${indexLocation}`}
        analyticsEventLocation={AnalyticsEventLocation.CATALOG}
        onInViewChange={(itemInView) =>
          onProductInViewChange(material, itemInView)
        }
        onProjectSelect={onProjectSelect}
        userLang={'en'}
        selectedVariants={selectedVariants}
        shouldShowVariants={false}
        additionalSplitProperties={{ srpCBATC: 'cb-atc' }}
        onColorVariantsClick={handleOnColorVariantsClick}
        isSelectboxDisabled={false}
        isCompareLimitReached={false}
        onAddToCart={handleAddToCart}
        onAddToFavorites={handleBoards}
        loadingCartItemIds={loadingIds}
      />
    );
  };

  return (
    <div className={styles.productGridWrapper}>
      <ul className={styles.productGrid}>
        {products.map((product, index) => (
          <li key={product.entityId} className={styles.productGridItem}>
            {renderItem(index)}
          </li>
        ))}
      </ul>
    </div>
  );
};
